import React from 'react';

export const Bone: React.FC<{ className?: string }> = ({ className = '' }) => (
  <div
    className={`animate-skeleton rounded bg-[length:200%_100%] bg-gradient-to-r from-[#e8edf2] via-[#f4f7fa] to-[#e8edf2] motion-reduce:animate-none ${className}`}
  />
);

type QuoteSkeletonKind = 'equity' | 'etf' | 'crypto';

const METRIC_ROW_COUNTS: Record<QuoteSkeletonKind, number> = {
  equity: 12,
  etf: 20,
  crypto: 12,
};

const tallMetricRow = (kind: QuoteSkeletonKind, index: number) => kind === 'equity' && (index === 9 || index === 10);

export const ChartPlotSkeleton: React.FC<{ heightClassName?: string; label?: string }> = ({
  heightClassName = 'h-[220px]',
  label = 'Loading chart',
}) => (
  <div className={heightClassName} aria-busy="true" aria-label={label}>
    <Bone className="h-full w-full" />
  </div>
);

export const ChartCardSkeleton: React.FC<{ titleWidth?: string; label?: string }> = ({
  titleWidth = 'w-32',
  label,
}) => (
  <div className="rounded border border-[#e9ecef] bg-white p-4">
    <Bone className={`mb-3 h-3 ${titleWidth}`} />
    <ChartPlotSkeleton label={label} />
  </div>
);

const KPIBoxSkeleton: React.FC = () => (
  <div className="flex flex-col gap-2 rounded border border-gray-100 bg-white px-4 py-3">
    <Bone className="h-2.5 w-20" />
    <Bone className="h-5 w-16" />
    <Bone className="h-2.5 w-12" />
  </div>
);

export const TableSkeleton: React.FC<{
  columns?: number;
  rows?: number;
  label?: string;
}> = ({ columns = 6, rows = 6, label = 'Loading table' }) => (
  <div className="overflow-x-auto rounded border border-[#e9ecef]" aria-busy="true" aria-label={label}>
    <div className="flex gap-4 border-b border-gray-100 bg-gray-50 px-3 py-2">
      {Array.from({ length: columns }, (_, index) => (
        <Bone key={`head-${index}`} className={`h-2.5 ${index === 0 ? 'w-24' : 'ml-auto w-12'}`} />
      ))}
    </div>
    {Array.from({ length: rows }, (_, rowIndex) => (
      <div key={`row-${rowIndex}`} className="flex gap-4 border-b border-gray-100 px-3 py-2.5">
        {Array.from({ length: columns }, (_, colIndex) => (
          <Bone key={`cell-${rowIndex}-${colIndex}`} className={`h-3 ${colIndex === 0 ? 'w-20' : 'ml-auto w-10'}`} />
        ))}
      </div>
    ))}
  </div>
);

export const PeerComparisonSkeleton: React.FC = () => (
  <div className="flex flex-col gap-y-8" aria-busy="true" aria-label="Loading peer data">
    <div className="rounded border border-[#e9ecef] bg-white p-4">
      <div className="mb-3 flex items-center justify-between">
        <Bone className="h-3 w-36" />
        <div className="flex gap-1">
          <Bone className="h-5 w-8" />
          <Bone className="h-5 w-8" />
          <Bone className="h-5 w-8" />
        </div>
      </div>
      <ChartPlotSkeleton label="Loading relative performance" />
    </div>

    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
      <ChartCardSkeleton titleWidth="w-36" label="Loading valuation chart" />
      <ChartCardSkeleton titleWidth="w-28" label="Loading margins chart" />
    </div>

    <TableSkeleton columns={8} rows={8} label="Loading peer comparison table" />
  </div>
);

export const FinancialsSkeleton: React.FC = () => (
  <div className="flex flex-col gap-y-8" aria-busy="true" aria-label="Loading financial data">
    <div className="flex justify-end gap-2">
      <Bone className="h-7 w-16" />
      <Bone className="h-7 w-14" />
    </div>

    <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-6">
      {Array.from({ length: 6 }, (_, index) => (
        <KPIBoxSkeleton key={index} />
      ))}
    </div>

    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
      <ChartCardSkeleton titleWidth="w-40" label="Loading revenue chart" />
      <ChartCardSkeleton titleWidth="w-28" label="Loading margin chart" />
    </div>

    <TableSkeleton columns={5} rows={8} label="Loading income statement" />
    <TableSkeleton columns={5} rows={5} label="Loading cash flow statement" />
  </div>
);

const KeyStatisticsSkeleton = ({ kind }: { kind: QuoteSkeletonKind }) => (
  <div className="card p-4 overflow-hidden">
    <Bone className="h-9 w-36" />
    {Array.from({ length: METRIC_ROW_COUNTS[kind] }, (_, index) => (
      <div
        key={index}
        className="flex min-h-8 w-full items-center justify-between border-b border-bzblue-300 py-1.5 last:border-b-0"
      >
        <Bone className={`${tallMetricRow(kind, index) ? 'h-6' : 'h-5'} w-24`} />
        <Bone className={`${tallMetricRow(kind, index) ? 'h-6' : 'h-5'} w-16 shrink-0`} />
      </div>
    ))}
  </div>
);

const AboutQuoteSkeleton = () => (
  <div className="card overflow-hidden p-4">
    <Bone className="mb-2 h-4 w-40" />
    <div className="mt-1.5 flex flex-col gap-0.5">
      <Bone className="h-3 w-16" />
      <Bone className="h-4 w-32" />
    </div>
    <div className="mt-1.5 flex flex-col gap-0.5">
      <Bone className="h-3 w-16" />
      <Bone className="h-4 w-32" />
    </div>
    <div className="mb-8 mt-2 flex flex-col gap-1.5">
      <Bone className="h-3.5 w-full" />
      <Bone className="h-3.5 w-full" />
      <Bone className="h-3.5 w-full" />
      <Bone className="h-3.5 w-2/3" />
    </div>
  </div>
);

export const OverviewSidebarSkeleton: React.FC<{ kind?: QuoteSkeletonKind }> = ({ kind = 'equity' }) => (
  <div
    className="flex w-full flex-col gap-4 overflow-hidden"
    aria-busy="true"
    aria-label="Loading quote sidebar"
  >
    <div className="metrics-desktop w-full">
      <KeyStatisticsSkeleton kind={kind} />
    </div>
    {kind === 'crypto' ? null : <AboutQuoteSkeleton />}
  </div>
);

export const OverviewEtfHeaderSkeleton: React.FC<{ showRankings?: boolean }> = ({ showRankings = false }) => (
  <div className="flex flex-col gap-4 lg:flex-row" aria-busy="true" aria-label="Loading ETF overview">
    {showRankings ? (
      <div className="flex flex-col md:flex-1">
        <Bone className="mb-2 h-4 w-32" />
        <Bone className="h-[106px] w-full" />
      </div>
    ) : null}
    <Bone className="mt-auto h-[106px] flex-1" />
  </div>
);

const NewsItemSkeleton = () => (
  <div className="rounded border border-bzblue-400 bg-[#F2F8FF66] p-4">
    <Bone className="mb-1 h-5 w-4/5" />
    <Bone className="mb-2 h-5 w-3/5" />
    <Bone className="mb-3 h-5 w-full" />
    <Bone className="h-4 w-28" />
  </div>
);

export const TABOOLA_QUOTE_MID_PAGE_CONTAINER_ID = 'taboola-mid-page-thumbnails---quotes';

export const TaboolaMidPageThumbnailsSkeleton: React.FC<{ className?: string }> = ({ className = '' }) => (
  <div
    aria-busy="true"
    aria-label="Loading sponsored recommendations"
    className={`min-h-[360px] bg-white md:min-h-[342px] ${className}`.trim()}
  >
    <div className="mb-4 flex items-center justify-between gap-4">
      <Bone className="h-6 w-32 max-w-[40%]" />
      <Bone className="h-3 w-36 max-w-[45%]" />
    </div>
    <div className="grid grid-cols-1 gap-4 md:grid-cols-3 md:gap-4">
      {Array.from({ length: 3 }, (_, index) => (
        <div className={`flex flex-col gap-3 ${index > 0 ? 'hidden md:flex' : ''}`} key={index}>
          <Bone className="aspect-[4/3] w-full" />
          <Bone className="h-4 w-full" />
          <Bone className="h-4 w-11/12" />
          <div className="mt-1 flex items-center justify-between gap-2">
            <Bone className="h-3 w-24" />
            <Bone className="h-8 w-20 shrink-0 rounded" />
          </div>
        </div>
      ))}
    </div>
  </div>
);

export const OverviewBelowChartSkeleton: React.FC<{ kind?: QuoteSkeletonKind }> = ({ kind = 'equity' }) => (
  <div className="flex flex-col gap-4 overflow-hidden" aria-busy="true" aria-label="Loading quote overview">
    <div className="metrics-mobile w-full">
      <KeyStatisticsSkeleton kind={kind} />
    </div>
    <div className="w-full">
      <Bone className="mb-2 h-7 w-72 max-w-full" />
      {kind === 'crypto' ? null : (
        <div className="mb-3 inline-flex gap-3.5 rounded bg-[#f2f8ff] p-1">
          <Bone className="h-6 w-14" />
          <Bone className="h-6 w-24" />
        </div>
      )}
      <div className="flex flex-col gap-4">
        {Array.from({ length: 5 }, (_, index) => (
          <NewsItemSkeleton key={index} />
        ))}
      </div>
      <Bone className="mx-auto mb-8 mt-8 h-10 w-40" />
      {kind === 'crypto' ? null : <div className="mt-8 min-h-[360px] md:min-h-[342px]" aria-hidden />}
    </div>
    {kind === 'equity' ? (
      <div className="card overflow-hidden" aria-hidden="true">
        <Bone className="mb-4 ml-auto h-8 w-40" />
        <Bone className="h-[325px] w-full" />
      </div>
    ) : null}
  </div>
);

export const OverviewBelowFoldSkeleton: React.FC = () => (
  <div className="card overflow-hidden p-4" aria-busy="true" aria-label="Loading quote details">
    <Bone className="mb-3 h-4 w-40" />
    <Bone className="mb-2 h-3 w-full" />
    <Bone className="mb-2 h-3 w-11/12" />
    <Bone className="h-3 w-3/4" />
  </div>
);

export const QuoteLogoSkeleton: React.FC = () => (
  <div
    className="company-logo ticker-logo h-16 w-16 shrink-0 rounded border border-[#d4dbe7] bg-white"
    aria-hidden
  />
);

export const CalendarTableSkeleton: React.FC = () => (
  <div className="w-full" aria-busy="true" aria-label="Loading calendar">
    <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
      <div className="flex gap-2">
        <Bone className="h-8 w-28" />
        <Bone className="h-8 w-24" />
      </div>
      <Bone className="h-8 w-40" />
    </div>
    <TableSkeleton columns={6} rows={8} label="Loading calendar rows" />
  </div>
);
